import React from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl, Grid, Typography, Stack,
  Divider, Switch, FormControlLabel, Avatar, Pagination, Tooltip, Paper
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { useToast } from '../../hooks/useToast';
import { brandsApi } from '../../utils/apiClient';

const STATUS_COLOR = {
  active: 'success',
  inactive: 'default',
  out_of_stock: 'warning',
};

const CATEGORY_OPTIONS = [
  'Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Books',
  'Toys & Games', 'Health & Beauty', 'Automotive', 'Art & Crafts', 'Other'
];
const CONDITION_OPTIONS = [{ value: 'new', label: 'New' }, { value: 'refurbished', label: 'Refurbished' }];
const STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'out_of_stock', label: 'Out of Stock' }];

const AdminProducts = ({
  products,
  productsLoading,
  productsError,
  productsPage,
  productsTotalPages,
  productsSearch,
  setProductsSearch,
  showProductModal,
  editingProduct,
  productForm,
  productFormError,
  productFormLoading,
  openProductModal,
  closeProductModal,
  handleProductFormSubmit,
  handleProductFormChange,
  handleDeleteProduct,
  handleApproveProduct,
  handleRejectProduct,
  handleBulkProductStatusUpdate,
  fetchProducts,
  productFormFiles,
  setProductFormFiles
}) => {
  const toast = useToast();

  // Image preview URLs
  const [imagePreviews, setImagePreviews] = React.useState([]);
  // Brands state
  const [brands, setBrands] = React.useState([]);
  
  // Category options for dropdown
  const categoryOptions = CATEGORY_OPTIONS;

  // Condition options
  const conditionOptions = CONDITION_OPTIONS;

  // Status options
  const statusOptions = STATUS_OPTIONS;

  // Fetch brands on mount
  React.useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await brandsApi.getAll({ isActive: true });
        setBrands(Array.isArray(response) ? response : (response?.data || []));
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  React.useEffect(() => {
    if (productFormFiles && productFormFiles.length > 0) {
      const urls = Array.from(productFormFiles).map(file => URL.createObjectURL(file));
      setImagePreviews(urls);
      return () => urls.forEach(url => URL.revokeObjectURL(url));
    } else {
      setImagePreviews([]);
    }
  }, [productFormFiles]);

  const handleFileChange = e => {
    const files = e.target.files;
    if (files && files.length > 5) {
      toast.warning('You can upload maximum 5 images');
      return;
    }
    setProductFormFiles(files);
  };

  const removeImagePreview = (index) => {
    const newFiles = Array.from(productFormFiles);
    newFiles.splice(index, 1);
    const dataTransfer = new DataTransfer();
    newFiles.forEach(file => dataTransfer.items.add(file));
    setProductFormFiles(dataTransfer.files);
  };

  const handleBulkStatusUpdate = async (productIds, newStatus) => {
    if (handleBulkProductStatusUpdate) {
      await handleBulkProductStatusUpdate(productIds, newStatus);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch('/api/products/export', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to export products');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Products Management"
        subheader={products.length > 0 ? `Page ${productsPage} of ${productsTotalPages} — ${products.length} products` : 'Manage platform product catalog'}
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}

      />
      {productsLoading && <LinearProgress color="primary" />}
      <CardContent sx={{ pt: 0 }}>
        {/* Toolbar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search by name, category, or tags\u2026"
            value={productsSearch}
            onChange={e => setProductsSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchProducts(productsPage)}><RefreshOutlinedIcon /></IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExportCSV} sx={{ borderRadius: 2 }}>
              Export CSV
            </Button>
            <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => openProductModal()} sx={{ borderRadius: 2 }}>
              Add Product
            </Button>
          </Stack>
        </Stack>

        {productsError && <Alert severity="error" sx={{ mb: 2 }}>{productsError}</Alert>}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Availability</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Reservable</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map(product => (
                <TableRow key={product._id} hover>
                  <TableCell>
                    {product.images && product.images.length > 0 ? (
                      <Avatar
                        src={product.images[0].thumbnailUrl || product.images[0].url}
                        alt={product.name}
                        variant="rounded"
                        sx={{ width: 44, height: 44 }}
                      />
                    ) : (
                      <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: 'action.hover' }}>
                        <InventoryOutlinedIcon fontSize="small" />
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{product.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{product.category}</Typography>
                    {product.subCategory && (
                      <Typography variant="caption" color="text.secondary">{product.subCategory}</Typography>
                    )}
                  </TableCell>
                  <TableCell>${product.price}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.soldOut ? 'Sold Out' : 'Available'}
                      size="small"
                      color={product.soldOut ? 'error' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.status}
                      size="small"
                      color={STATUS_COLOR[product.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize', color: 'text.secondary', fontSize: '0.8rem' }}>
                    {product.condition}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.isReservable ? 'Yes' : 'No'}
                      size="small"
                      color={product.isReservable ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => openProductModal(product)} title="Edit">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteProduct(product)} title="Delete">
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!productsLoading && products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    <Stack alignItems="center" spacing={1}>
                      <InventoryOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      <Typography variant="body2">No products found</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {productsTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={productsTotalPages}
              page={productsPage}
              onChange={(_, p) => fetchProducts(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </CardContent>

      {/* Product Form Dialog */}
      <Dialog
        open={showProductModal}
        onClose={closeProductModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryOutlinedIcon />
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Box>
          <IconButton onClick={closeProductModal} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleProductFormSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Basic Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Product Name *" name="name" value={productForm.name} onChange={handleProductFormChange} required />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" multiline rows={3} label="Description *" name="description" value={productForm.description} onChange={handleProductFormChange} required inputProps={{ minLength: 10 }} helperText="Min 10 characters. Describe key features, specs, and notes." />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Category *</InputLabel>
                      <Select name="category" value={productForm.category} label="Category *" onChange={handleProductFormChange}>
                        <MenuItem value=""><em>Select Category</em></MenuItem>
                        {categoryOptions.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Sub Category" name="subCategory" value={productForm.subCategory} onChange={handleProductFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Brand</InputLabel>
                      <Select name="brand" value={productForm.brand || ''} label="Brand" onChange={handleProductFormChange}>
                        <MenuItem value=""><em>Select Brand (Optional)</em></MenuItem>
                        {brands.map(brand => <MenuItem key={brand._id} value={brand._id}>{brand.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Condition *</InputLabel>
                      <Select name="condition" value={productForm.condition} label="Condition *" onChange={handleProductFormChange}>
                        {conditionOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status *</InputLabel>
                      <Select name="status" value={productForm.status} label="Status *" onChange={handleProductFormChange}>
                        {statusOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!productForm.isReservable}
                          onChange={e => handleProductFormChange({ target: { name: 'isReservable', value: e.target.checked } })}
                        />
                      }
                      label="Reservable (show Reserve button and allow auction rooms)"
                    />
                    {productForm.isReservable && (
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" type="number" label="Participation Fee (TND)" name="participationFee" value={productForm.participationFee || ''} onChange={handleProductFormChange} inputProps={{ min: 0, step: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" type="number" label="Free Reservation Limit" name="reservationParticipantLimit" value={productForm.reservationParticipantLimit || ''} onChange={handleProductFormChange} inputProps={{ min: 0, step: 1 }} placeholder="0 (default 100 cap)" />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* Pricing & Inventory */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Pricing & Inventory</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="number" label="Price ($) *" name="price" value={productForm.price} onChange={handleProductFormChange} required inputProps={{ min: 0, step: 0.01 }} />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!productForm.soldOut}
                          onChange={e => handleProductFormChange({ target: { name: 'soldOut', value: e.target.checked } })}
                          color="error"
                        />
                      }
                      label="Mark as Sold Out"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Images */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Product Images</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Upload up to 5 images (JPG, PNG, WebP — max 10 MB each)
                </Typography>
                <input type="file" name="images" id="images" multiple accept="image/*" onChange={handleFileChange} style={{ marginBottom: 8 }} />
                {imagePreviews.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                    {imagePreviews.map((url, idx) => (
                      <Box key={idx} sx={{ position: 'relative' }}>
                        <Avatar src={url} alt={`preview-${idx}`} variant="rounded" sx={{ width: 72, height: 72 }} />
                        <IconButton
                          size="small"
                          onClick={() => removeImagePreview(idx)}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: '#fff', width: 20, height: 20, '&:hover': { bgcolor: 'error.dark' } }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              {/* Additional Info */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Additional Information</Typography>
                <TextField fullWidth size="small" label="Tags (comma separated)" name="tags" value={productForm.tags} onChange={handleProductFormChange} placeholder="electronics, smartphone, apple" helperText="Separate tags with commas to help customers find this product." />
              </Paper>

              {/* Physical Dimensions */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Physical Dimensions (Optional)</Typography>
                <Grid container spacing={2}>
                  {['weight', 'length', 'width', 'height'].map(dim => (
                    <Grid item xs={6} sm={3} key={dim}>
                      <TextField fullWidth size="small" type="number" label={`${dim.charAt(0).toUpperCase() + dim.slice(1)} ${dim === 'weight' ? '(kg)' : '(cm)'}`} name={dim} value={productForm[dim]} onChange={handleProductFormChange} inputProps={{ min: 0, step: 0.01 }} />
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {productFormError && <Alert severity="error">{productFormError}</Alert>}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeProductModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={productFormLoading}>
              {productFormLoading ? 'Saving…' : (editingProduct ? 'Update Product' : 'Create Product')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
};

export default AdminProducts;
